import {
  ensureContrast,
  getLightenedColor,
  getShadedColor,
} from '@ardoq/color-helpers';
import { lighten } from 'polished';
import { colors } from '@ardoq/design-tokens';
import { NO_EXPORT_CLASS_NAME } from '@ardoq/global-consts';
import {
  ComponentRepresentation,
  type ComponentRepresentationData,
} from '@ardoq/basic-components';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { type SVGAttributes, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { CONTEXT_HIGHLIGHT_PADDING } from '../../../yfilesExtensions/styles/consts';
import {
  ContextHighlight,
  SelectedRelatedHighlight,
} from 'yfilesExtensions/styles/atoms';
import { GRAPH_NODE_ID } from '../../consts';

/**
 * Using button to make it accessible for keyboard users and screen readers.
 */
const IconButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;

  width: 100%;
  height: 100%;
  border-radius: 50%;

  background: transparent;
  border: none;
  padding: 3px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.blue400};
    outline-offset: -2px;
  }
`;

const PulsingBlob = styled.circle`
  fill: transparent;
  stroke: ${colors.blue600};
  stroke-width: 2;
  pointer-events: none;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      opacity: 1;
      stroke-width: 2;
    }

    70% {
      opacity: 0;
      stroke-width: 10;
    }

    100% {
      opacity: 0;
      stroke-width: 2;
    }
  }
`;

const GroupWithTransition = styled.g<{ $hoverColor: string }>`
  transition: opacity 0.5s;
  cursor: pointer;

  &:hover circle.node-border {
    fill: ${props => props.$hoverColor};
    cursor: pointer;
  }
`;

interface ArdoqNodeCloneProperties {
  representationData?: ComponentRepresentationData | null;
  color: string;
  isContext: boolean;
  isSelectedRelated: boolean;
  width: number;
  height: number;
  contextHighlightPadding: typeof CONTEXT_HIGHLIGHT_PADDING | 0;
  graphId: string;
  isSelected?: boolean;
  hasFilters?: boolean;
  selectionRadiusDiff?: number;
  selectionStrokeWidth?: number;
  opacity?: number;
  label: string | undefined;
  shouldDisplayClickOtherComponentsHint: boolean;
}
// TODO AM it would be great to have it in a package and write visual tests for it
const ArdoqNodeClone = ({
  representationData,
  isContext,
  isSelectedRelated,
  width,
  height,
  contextHighlightPadding,
  color,
  graphId,
  isSelected,
  selectionRadiusDiff = 0,
  selectionStrokeWidth = 0,
  hasFilters = false,
  opacity = 1,
  label = '',
  shouldDisplayClickOtherComponentsHint = false,
  ...rest
}: ArdoqNodeCloneProperties & SVGAttributes<SVGGElement>) => {
  const shadedColor = getShadedColor(color);
  const lightenedColor = getLightenedColor(color);
  const contrastColor = ensureContrast(lightenedColor, color);
  // Compute hover color for Safari compatibility (CSS filter doesn't work reliably)
  const hoverColor = lighten(0.1, lightenedColor);

  /**
   * This effect is used to restart the pulsing animation.
   */
  useLayoutEffect(() => {
    if (shouldDisplayClickOtherComponentsHint) {
      const elements =
        document.querySelectorAll<SVGCircleElement>('.pulsing-blob');
      elements.forEach(element => {
        element.style.animation = 'none';
        setTimeout(() => {
          element.style.animation = ''; // Restores animation from CSS
        });
      });
    }
  }, [shouldDisplayClickOtherComponentsHint]);

  return (
    <>
      {isContext && (
        <ContextHighlight
          className={NO_EXPORT_CLASS_NAME}
          cx={width / 2}
          cy={height / 2}
          rx={width / 2 + contextHighlightPadding}
          ry={height / 2 + contextHighlightPadding}
        />
      )}
      {isSelectedRelated && (
        <SelectedRelatedHighlight
          className={NO_EXPORT_CLASS_NAME}
          cx={width / 2}
          cy={height / 2}
          rx={width / 2}
          ry={height / 2}
        />
      )}
      <GroupWithTransition
        {...{
          ...rest,
          [GRAPH_NODE_ID]: graphId,
        }}
        opacity={opacity}
        $hoverColor={hoverColor}
      >
        {representationData && (
          <>
            {isSelected && (
              <circle
                cx={width / 2}
                cy={width / 2}
                r={width / 2 - selectionStrokeWidth / 2}
                fill="transparent"
                strokeDasharray="10"
                strokeWidth="4"
                stroke={colors.blue400}
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from={`0 ${width / 2} ${width / 2}`}
                  to={`360 ${width / 2} ${width / 2}`}
                  dur="20s"
                  repeatCount="indefinite"
                />
              </circle>
            )}
            <circle
              className="node-border"
              cx={width / 2}
              cy={width / 2}
              r={width / 2 - (selectionRadiusDiff + selectionStrokeWidth / 2)}
              fill={lightenedColor}
              strokeWidth="1"
              stroke={shadedColor}
              style={{ transition: 'fill 0.2s ease' }}
            />
            <foreignObject
              y={0}
              x={0}
              height={height}
              width={width}
              style={{ overflow: 'visible' }} // for the pulsing shadow
            >
              <IconButton tabIndex={0} aria-label={label}>
                <ComponentRepresentation
                  isImage={representationData.isImage ?? false}
                  value={representationData.value ?? null}
                  icon={representationData.icon}
                  iconSize={40 as IconSize}
                  style={{
                    fontSize: 40,
                    color: contrastColor,
                  }}
                />
                <VisuallyHidden>{label}</VisuallyHidden>
              </IconButton>
            </foreignObject>
            {hasFilters && (
              <FilterIcon
                parentRadius={width}
                padding={selectionRadiusDiff + selectionStrokeWidth}
                xOffset={0}
                yOffset={0}
              />
            )}
            {isSelected || !shouldDisplayClickOtherComponentsHint ? null : (
              <PulsingBlob
                className="pulsing-blob"
                cx={width / 2}
                cy={height / 2}
                r={(width / 2) * 0.85}
              />
            )}
          </>
        )}
      </GroupWithTransition>
    </>
  );
};

const FilterIcon = ({
  parentRadius,
  padding,
  xOffset,
  yOffset,
}: {
  parentRadius: number;
  padding: number;
  xOffset: number;
  yOffset: number;
}) => {
  const iconWrapperRadius = 12;
  const xCoordinate =
    xOffset + 0.7071 * (parentRadius + padding) - iconWrapperRadius; // 0.707 is the sin of 45 degrees
  const yCoordinate =
    yOffset +
    parentRadius -
    0.7071 * (parentRadius + padding) -
    iconWrapperRadius;

  return (
    <g transform={`translate(${xCoordinate}, ${yCoordinate})`}>
      <circle
        cx={iconWrapperRadius}
        cy={iconWrapperRadius}
        r={iconWrapperRadius}
        fill={colors.brand900}
      />
      <foreignObject
        x={0}
        y={0}
        width={iconWrapperRadius * 2}
        height={iconWrapperRadius * 2}
        style={{ pointerEvents: 'none' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <Icon
            iconName={IconName.FILTER_LIST}
            color={colors.white950}
            iconSize={IconSize.MEDIUM}
          />
        </div>
      </foreignObject>
    </g>
  );
};

export { ArdoqNodeClone };
